import type { GetCurrentMemberFn } from '@/features/iam'
import type { AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job, type Collaborator } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { Id } from '@/infra/id'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { mustHaveMaxLength } from '@/utils/validx-util'
import { required, validationContext } from 'validx'

/**
 * View Model for the Remove Collaborators as Admin dialog.
 */
export class RemoveCollaboratorsAsAdminDialogViewModel {
  /**
   * Hold the maximum length of reason field.
   */
  private readonly _reasonLength = {
    reason: { max: 100 },
  }

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Collaborator to remove.
   */
  @observable accessor collaborator: Collaborator | undefined = undefined

  /**
   * Disables submit button.
   */
  @observable accessor disabled = true

  /**
   * The reason for removing the collaborator.
   */
  @observable accessor reason = ''

  /**
   * Helper text for reason input field.
   */
  readonly reasonHelpText = `Why are you removing the collaborator?
    This text is public and can be seen by both Client and Pros.`

  /**
   * Validation context for View Model.
   */
  readonly validation = validationContext(this, {
    collaborator: [required('Collaborator is required.')],
    reason: [
      mustHaveMaxLength(
        this._reasonLength.reason.max,
        `Reason must be less than ${this._reasonLength.reason.max} characters.`,
      ),
    ],
  })

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {}

  /**
   * Supporting Collaborators on the Job.
   */
  @computed
  get supportingCollaborators() {
    if (this.canOnlyRemoveClients) {
      return this._job.supportingClients
    }

    return this._job.supportingPros.concat(this._job.supportingClients)
  }

  /**
   * Filtered collaborators.
   */
  @computed
  get filteredCollaborators() {
    return this.supportingCollaborators.filter((supportingCollaborator) =>
      this._job.collaborators.some(
        (c) =>
          c.member === supportingCollaborator.member && c.role === 'Supporting',
      ),
    )
  }

  /**
   * Indicates if the user can only remove clients.
   */
  @computed
  get canOnlyRemoveClients() {
    return (
      !this._getCurrentMember(this._currentMemberWorkspaceId).permissions
        .adminUserList &&
      this._getCurrentMember(this._currentMemberWorkspaceId).permissions
        .adminClientUserList
    )
  }

  @computed
  private get _currentMemberWorkspaceId() {
    return Id.assert(
      this._getCurrentMember(this._job.workspace.id).workspace.id,
    )
  }

  /**
   * Sets a collaborator to remove.
   */
  @action.bound
  setCollaborator(collaborator?: Collaborator) {
    if (this.collaborator === collaborator) {
      return
    }

    this.collaborator = collaborator
    this.disabled = !this.validate()
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.collaborator = undefined
    this.reason = ''
    this.disabled = true

    this.removeCollaboratorAsAdmin.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Removes the collaborator as Admin.
   */
  removeCollaboratorAsAdmin = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const collaborator = this._job.collaborators.find(
      (c) => c.member === this.collaborator?.member,
    )

    if (collaborator) {
      const alert = this._newAlert()
        .withState('pending')
        .saying(
          'Removing Collaborator',
          `Removing ${collaborator.member.displayName} from ${this._job.name}...`,
        )
        .show()

      try {
        await this._jobStore.removeCollaboratorAsAdmin({
          jobId: this._job.id,
          collaboratorUserPublicId: collaborator.member.user.publicId,
          reason: this.reason,
        })
      } catch (err) {
        alert.dismiss()
        throw err
      }

      alert
        .withState('success')
        .saying(
          'Success!',
          `${collaborator.member.displayName} was removed from ${this._job.name}.`,
        )
        .autoDismiss()
    }

    this.modal.close()
  })

  /**
   * Shows Removes Collaborators as Admin Dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.modal.show()
  }
}
